import React, { useEffect } from 'react';
import loadable from '@loadable/component';
import { useAppStateContext } from '../../lib/AppStateProvider';
import { get } from '../../lib/getPropertyByPath';
import './FooterContainer.css';

const Footer: any = loadable(
  () => import(/* webpackChunkName: "Footer" */ '@dtcm/ui-components/dist/components/18_Footer/Footer')
);
const NewsletterForm: any = loadable(
  () =>
    import(
      /* webpackChunkName: "NewsletterForm" */ '@dtcm/ui-components/dist/components/32_NewsletterForm/NewsletterForm'
    )
);
const DownloadBox: any = loadable(
  () =>
    import(/* webpackChunkName: "DownloadBox" */ '@dtcm/ui-components/dist/components/18_Footer/DownloadBox')
);
const FooterWeatherWidget: any = loadable(
  () =>
    import(
      /* webpackChunkName: "FooterWeatherWidget" */ '@dtcm/ui-components/dist/components/18_FooterWeatherWidget/FooterWeatherWidget'
    )
);

export const FooterContainer = () => {
  const { appState } = useAppStateContext();
  const [isChina, setIsChina] = React.useState<Boolean>(false);

  useEffect(() => {
    const urlParams: any = !!(
      typeof window !== 'undefined' &&
      window.document &&
      window.document.createElement
    )
      ? new URLSearchParams(document.location.search)
      : new URLSearchParams('');
    const isC = Boolean(urlParams.get('isChina') === 'true');
    setIsChina(isC);
  });
  if (!appState.footer || get(appState, 'dictionary.viewBag.isEmptyLayout', false)) {
    return null;
  }

  const { footer, downloadBox, newsletterForm, footerWeatherWidget } = appState;

  // Map data received from Sitecore to props expected by Footer component.
  const processLinkContainer = (linksContainer: any) => {
    if (!linksContainer) {
      return null;
    }
    return {
      name: get(linksContainer, 'name.value'),
      links: linksContainer.children.map((l: any) => {
        return {
          title: get(l, 'title.value'),
          link: get(l, 'link.value'),
          name: l.name,
        };
      }),
    };
  };

  let downloadBoxProps = undefined;
  if (downloadBox) {
    downloadBoxProps = {
      title: get(downloadBox, 'title.value'),
      icons: get(downloadBox, 'icons', []).map((icon: any) => {
        return {
          text: get(icon, 'text.value'),
          link: get(icon, 'link.value'),
          linkIcon: get(icon, 'linkIcon.value'),
          analyticsLabel: get(icon, 'analyticsLabel.value'),
        };
      }),
      analytics: {
        downloadAppsAnalytics: get(appState, 'analytics.downloadAppsAnalytics'),
        richTextAnalytics: get(appState, 'analytics.richTextAnalytics'),
      },
    };
  }

  const footerProps = {
    logo: get(footer, 'logo.value'),
    logoLink: get(footer, 'logoLink.value'),
    copyright: get(footer, 'copyright.value'),
    bottomText: get(footer, 'bottomText.value'),
    bottomImage: get(footer, 'bottomImage.value'),
    bottomImageLink: get(footer, 'bottomImageLink.value'),
    footerLinks: get(footer, 'footerLinks.targetItem.children', []).map((lc: any) =>
      processLinkContainer(lc)
    ),
    socialMediaLinks: get(footer, 'socialMediaLinks.targetItem.children', []).map((l: any) => {
      return {
        name: l.name,
        title: get(l, 'title.value'),
        link: get(l, 'link.value'),
        linkIcon: get(l, 'linkIcon.value'),
        isChina: get(l, 'isChina.value'),
      };
    }),
    hygieneLinks: get(footer, 'hygieneLinks.targetItem.children', []).map((l: any) => {
      return {
        name: l.name,
        title: get(l, 'title.value'),
        link: get(l, 'link.value'),
      };
    }),
    analytics: get(appState, 'analytics'),
    accessWidget: get(appState, 'accessWidget'),
  };

  let newsletterProps = undefined;
  if (newsletterForm) {
    newsletterProps = {
      fields: {
        newsletterAnalytics: get(appState, 'analytics.newsletterAnalytics'),
        richTextAnalytics: get(appState, 'analytics.richTextAnalytics'),
        data: {
          item: {
            heading: get(newsletterForm, 'heading.value'),
            subHeading: get(newsletterForm, 'subHeading.value'),
            footerText: get(newsletterForm, 'footerText.value'),
            emailPlaceholderText: get(newsletterForm, 'emailPlaceholderText.value'),
            submitButtonText: get(newsletterForm, 'submitButtonText.value'),
            submittingButtonText: get(newsletterForm, 'submittingButtonText.value'),
            successSubmissionHeading: get(newsletterForm, 'successSubmissionHeading.value'),
            successSubmissionSubheading: get(newsletterForm, 'successSubmissionSubheading.value'),
            errorSubmissionHeading: get(newsletterForm, 'errorSubmissionHeading.value'),
            errorSubmissionSubheading: get(newsletterForm, 'errorSubmissionSubheading.value'),
            fieldsOfInterestTitle: get(newsletterForm, 'fieldsOfInterestTitle.value'),
            fieldsOfInterest: get(newsletterForm, 'fieldsOfInterest.values'),
            sfIntegrationEnable: get(newsletterForm, 'sfIntegrationEnable.jss.value'),
            sfApiUrl: get(newsletterForm, 'sfApiUrl.jss.value'),
            sfFormId: get(newsletterForm, 'sfFormId.jss.value'),
            brandname: get(newsletterForm, 'brandname.jss.value'),
          },
        },
      },
    };
  }

  const footerWeatherWidgetProps = {
    heading: get(footerWeatherWidget, 'heading.value'),
    weatherPageUrl: get(footerWeatherWidget, 'weatherPageUrl.value'),
    weatherPageLabel: get(footerWeatherWidget, 'weatherPageLabel.value'),
    errorMessage: get(footerWeatherWidget, 'errorMessage.value'),
    weatherEnable: get(footerWeatherWidget, 'weatherEnable.value'),
    apiKey: get(footerWeatherWidget, 'apiKey.value'),
    apiUrlConditions: get(footerWeatherWidget, 'apiUrlConditions.value'),
    apiUrl: get(footerWeatherWidget, 'apiUrl.value'),
    defaultLocation: get(footerWeatherWidget, 'defaultLocation.value'),
    dictionary: get(footerWeatherWidget, 'dictionary'),
    weatherWidgetAnalytics: get(footerWeatherWidget, 'weatherWidgetAnalytics'),
  };

  return (
    <footer className="footer overflowXClip">
      <FooterWeatherWidget {...footerWeatherWidgetProps} />
      {!isChina ? (
        <section className="stay-update-section">
          <div className="module-group no-padding-custom">
            <div className="module no-padding-custom">
              <div className="container">
                <div className="row">
                  <div className="col-md-6 col-lg-7">
                    {newsletterProps && <NewsletterForm {...newsletterProps} />}
                  </div>
                  <div className="col-md-6 col-lg-5">
                    {downloadBoxProps && <DownloadBox {...downloadBoxProps} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}
      <Footer {...footerProps} />
    </footer>
  );
};

// default exports are encouraged for dynamic imports
export default FooterContainer;
